import { useMutation, useQuery } from '@tanstack/react-query'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import React, { ReactElement, useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import {
  RECEIPT_RECOVERY_MOCK_ACCOUNTING_PERIODS,
  RECEIPT_RECOVERY_MOCK_STATS_BY_PERIOD,
} from 'src/feature/ReceiptRecovery/receiptRecovery.fixtures'
import { ReceiptRecoveryTransactionState } from 'src/feature/ReceiptRecovery/receiptRecovery.types'
import ReceiptRecoveryWorkspace from 'src/feature/ReceiptRecovery/ReceiptRecoveryWorkspace'
import { useTranslate } from 'src/localization/Trns'
import { TransactionReceiptRecoveryStatus } from 'src/models/transaction.models'
import { accountingPeriodQueries } from 'src/queries/organizations/accountingPeriods/accountingPeriods.queries'
import {
  receiptAgentMutations,
  receiptAgentQueries,
} from 'src/queries/organizations/receiptAgent/receiptAgent.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectUserExplicitOrganizationIdFromState } from 'src/redux/selectors/data/organizations.selectors'

import { getReceiptRecoveryTransactionsUrl } from './receiptRecoveryNavigation'
import * as style from './ReceiptRecoveryPage.style'

const transactionStatusForReceiptState: Record<
  ReceiptRecoveryTransactionState,
  TransactionReceiptRecoveryStatus
> = {
  'needs-review': TransactionReceiptRecoveryStatus.NeedsReview,
  'waiting-on-spender': TransactionReceiptRecoveryStatus.WaitingOnSpender,
  'receipt-reconciled': TransactionReceiptRecoveryStatus.ReceiptReconciled,
}

const ReceiptRecoveryPage = (): ReactElement => {
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>()
  const history = useHistory()
  const organizationId = useSelector(selectUserExplicitOrganizationIdFromState)
  const remoteConfig = useSelector(selectFeatureFlaggerFromState)
  const translate = useTranslate()
  const dashboardEnabled = remoteConfig.enable_receipt_recovery_dashboard
  const mockDataEnabled = remoteConfig.enable_receipt_recovery_mock_data
  const accountingPeriodsQueryOptions =
    accountingPeriodQueries.list(organizationId)
  const accountingPeriodsQuery = useQuery({
    ...accountingPeriodsQueryOptions,
    enabled:
      accountingPeriodsQueryOptions.enabled &&
      dashboardEnabled &&
      !mockDataEnabled,
  })
  const accountingPeriods = accountingPeriodsQuery.data
  const receiptAgentQueryOptions = receiptAgentQueries.status(organizationId)
  const receiptAgentQuery = useQuery({
    ...receiptAgentQueryOptions,
    enabled:
      receiptAgentQueryOptions.enabled && dashboardEnabled && !mockDataEnabled,
  })
  const periods = mockDataEnabled
    ? RECEIPT_RECOVERY_MOCK_ACCOUNTING_PERIODS
    : (accountingPeriods ?? [])
  const currentPeriod = periods.find((period) => period.current)
  const resolvedSelectedPeriod =
    periods.find((period) => period.id === selectedPeriodId) ?? currentPeriod
  const selectedPeriodStatsQueryOptions = accountingPeriodQueries.stats(
    organizationId,
    resolvedSelectedPeriod?.id,
  )
  const selectedPeriodStatsQuery = useQuery({
    ...selectedPeriodStatsQueryOptions,
    enabled:
      selectedPeriodStatsQueryOptions.enabled &&
      dashboardEnabled &&
      !mockDataEnabled,
  })
  const selectedPeriodStats = selectedPeriodStatsQuery.data
  const isRunAvailable =
    !mockDataEnabled && receiptAgentQuery.data?.canRunReceiptRecovery === true
  const { isPending: isRunPending, mutate: startReceiptAgentRun } = useMutation(
    receiptAgentMutations.startRun(),
  )

  const handleOpenTransactions = useCallback(
    (state: ReceiptRecoveryTransactionState): void => {
      if (!resolvedSelectedPeriod) return

      history.push(
        getReceiptRecoveryTransactionsUrl(
          transactionStatusForReceiptState[state],
          resolvedSelectedPeriod,
        ),
      )
    },
    [history, resolvedSelectedPeriod],
  )

  const handleRunReceiptRecovery = useCallback((): void => {
    if (!isRunAvailable || !organizationId || !resolvedSelectedPeriod) return
    startReceiptAgentRun({
      organizationId,
      accountingPeriodId: resolvedSelectedPeriod.id,
    })
  }, [
    isRunAvailable,
    organizationId,
    resolvedSelectedPeriod,
    startReceiptAgentRun,
  ])

  const mockStats =
    mockDataEnabled && resolvedSelectedPeriod
      ? RECEIPT_RECOVERY_MOCK_STATS_BY_PERIOD[resolvedSelectedPeriod.id]
      : undefined
  const receiptStats = mockDataEnabled
    ? mockStats
    : selectedPeriodStats?.closingStats.receiptStats

  if (!dashboardEnabled) {
    return <NotFoundBackgroundPage />
  }
  if (
    !mockDataEnabled &&
    (accountingPeriodsQuery.isError ||
      selectedPeriodStatsQuery.isError ||
      (!accountingPeriodsQuery.isPending && !resolvedSelectedPeriod))
  ) {
    return (
      <LayoutPage css={style.layout} padding="none">
        <div css={style.pageContent}>
          <AlertPanel role="alert" variant="severe">
            {translate('We could not load Receipt Recovery. Please try again.')}
          </AlertPanel>
        </div>
      </LayoutPage>
    )
  }
  if (!resolvedSelectedPeriod || !receiptStats) {
    return <LoadingBackgroundPage />
  }

  return (
    <LayoutPage css={style.layout} padding="none">
      <div css={style.pageContent}>
        <ReceiptRecoveryWorkspace
          accountingPeriods={periods}
          isRunAvailable={isRunAvailable}
          isRunPending={isRunPending}
          mockMissingReceiptCount={mockStats?.missingReceipt}
          onOpenTransactions={handleOpenTransactions}
          onPeriodSelect={setSelectedPeriodId}
          onRunReceiptRecovery={handleRunReceiptRecovery}
          receiptStats={receiptStats}
          selectedPeriodId={resolvedSelectedPeriod.id}
        />
      </div>
    </LayoutPage>
  )
}

export default ReceiptRecoveryPage
